import React, { useCallback } from "react";
import { ProfitLossReportDto } from "@common/dto/reports/reporting-tab-profit-loss-response.dto";
import { NonUndefined } from "@common/helpers/typescript";
import { ReportFilters } from "@common/types/filters/reports";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { useCashFlowContext } from "./cash-flow.context";
import { CashFlowDrillDownContextProvider } from "./CashFlowDrillDownContextProvider";
import { CashFlowDrillDownDrawer } from "./CashFlowDrillDownDrawer";
import {
    CashFlowReportChart,
    CashFlowReportChartProps,
} from "./CashFlowReportChart";
import { CashFlowReportTableContainer } from "./CashFlowReportTable/CashFlowReportTableContainer";

interface CashFlowReportProps {
    resetFilters: () => void;
    onChange(filters: Partial<ReportFilters>): void;
}

export const CashFlowReport: React.FC<CashFlowReportProps> = ({
    resetFilters,
    onChange,
}) => {
    const {
        cashFlowReport,
        isCashFlowReportLoading,
        cashFlowFiltersUsed,
        reportCreatedAt,
    } = useCashFlowContext();

    const setCategoryFilter = useCallback<
        NonUndefined<CashFlowReportChartProps["setCategoryFilter"]>
    >(
        (categories) => {
            onChange({ category: categories });
        },
        [onChange],
    );

    if (isCashFlowReportLoading) {
        return <Loader />;
    }

    return cashFlowReport?.chart.series.length &&
        cashFlowFiltersUsed &&
        reportCreatedAt ? (
        <>
            <CashFlowReportChart
                report={cashFlowReport as unknown as ProfitLossReportDto}
                filters={cashFlowFiltersUsed}
                reportCreatedAt={reportCreatedAt}
                setCategoryFilter={setCategoryFilter}
            />
            <CashFlowDrillDownContextProvider>
                <CashFlowReportTableContainer
                    report={cashFlowReport}
                    filters={cashFlowFiltersUsed}
                    withBorder
                    boldTotals
                />
                <CashFlowDrillDownDrawer
                    tableData={cashFlowReport.tableData}
                    filters={cashFlowFiltersUsed}
                />
            </CashFlowDrillDownContextProvider>
        </>
    ) : (
        <div className="reporting-tab__empty-state">
            <EmptyState
                header="No matching transactions"
                body="The filters can't reproduce a report"
                ctaText="Clear filters"
                action={resetFilters}
                buttonVariant="primary"
            />
        </div>
    );
};
